import { useEffect, useState, type KeyboardEvent } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { useCustomerSearch } from './useCustomerSearch';

/**
 * Existing customers matching what is being typed in a name field, with the keyboard handling for the list
 * (CustomerSuggestList): ↑ ↓ move, Enter picks the highlighted one, Escape closes the list. Only existing customers
 * are offered; with no match the field stays a plain name. `enabled` is false while the field isn't being typed in,
 * so nothing is searched.
 */
export function useCustomerSuggest(query: string, enabled: boolean, onPick: (customer: CustomerDTO) => void) {
  const { results } = useCustomerSearch(enabled ? query : '');
  const [active, setActive] = useState(-1);
  const [closed, setClosed] = useState(false);

  // Typing again reopens a list closed with Escape; so does coming back to the field and typing the same text.
  useEffect(() => {
    setActive(-1);
    setClosed(false);
  }, [query, enabled]);

  const open = enabled && !closed && results.length > 0 && query.trim() !== '';

  /** True when the key was used by the list (the field should then leave it alone). */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): boolean => {
    if (!open) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + results.length + (i < 0 && step < 0 ? 1 : 0)) % results.length);
      return true;
    }
    if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      onPick(results[active]);
      return true;
    }
    if (e.key === 'Escape') {
      // Only the list closes: not the modal the field may be in.
      e.nativeEvent.stopImmediatePropagation();
      setClosed(true);
      return true;
    }
    return false;
  };

  return { open, results, active, onKeyDown, close: () => setClosed(true) };
}
